/*
 * /details/$patientId (page)
 */
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Container,
	Page,
	Placeholder,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { Plus } from "lucide-react"
import { PatientForm } from "../../features/patient/patient-form"
import { AddressForm } from "../../features/address/address-form"
import { PatientHeader } from "../../features/patient/patient-header"
import { trpc } from "../../trpc"
import type { Address } from "@emr/db/client"

export const Route = createFileRoute("/details/$patientId")({
	component: DetailsPage,
})

const MOCK_PATIENT = {
	firstName: "...",
	lastName: "",
	dateOfBirth: null,
	bloodType: null,
	allergies: null,
	emergencyContactName: null,
	emergencyContactPhone: null,
}

const ADDRESS_TYPE_LABELS: Record<string, string> = {
	home: "Home",
	work: "Work",
	billing: "Billing",
	shipping: "Shipping",
	other: "Other",
}

function DetailsPage() {
	const { patientId } = Route.useParams()
	const [editingAddressId, setEditingAddressId] = useState<string | null>(null)
	const [showAddAddress, setShowAddAddress] = useState(false)

	const { data: patient, isLoading: patientLoading } =
		trpc.patient.getByFilter.useQuery({ where: { id: patientId } })

	const { data: addresses, isLoading: addressesLoading } =
		trpc.address.getAllByFilter.useQuery({ where: { patientId, active: true } })

	const deleteAddress = trpc.address.update.useMutation({
		onSuccess: () => {
			utils.address.getAllByFilter.invalidate()
			toast.success("Address removed")
		},
	})
	const utils = trpc.useUtils()

	const isLoading = patientLoading || addressesLoading
	const displayPatient = patient ?? (MOCK_PATIENT as any)

	return (
		<Page centerVertical={false}>
			<PatientHeader patient={displayPatient} patientId={patientId} isDirty={false} />
			<Container width="xl" padding="lg" gap="lg">
				{isLoading ? (
					<Placeholder title="Loading..." />
				) : (
					<>
						{/* Personal information */}
						<Card>
							<CardHeader>
								<CardTitle>Personal Information</CardTitle>
							</CardHeader>
							<CardContent>
								<PatientForm patient={patient ?? undefined} />
							</CardContent>
						</Card>

						{/* Addresses */}
						<Card>
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle>Addresses</CardTitle>
									{!showAddAddress && (
										<Button
											size="sm"
											variant="outline"
											onClick={() => {
												setEditingAddressId(null)
												setShowAddAddress(true)
											}}
										>
											<Plus className="w-4 h-4 mr-1" />
											Add Address
										</Button>
									)}
								</div>
							</CardHeader>
							<CardContent className="flex flex-col gap-4">
								{/* Add form */}
								{showAddAddress && (
									<div className="border rounded-lg p-4">
										<p className="text-sm font-medium mb-3">New Address</p>
										<AddressForm
											patientId={patientId}
											onSuccess={() => {
												setShowAddAddress(false)
												utils.address.getAllByFilter.invalidate()
											}}
										/>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											className="mt-2"
											onClick={() => setShowAddAddress(false)}
										>
											Cancel
										</Button>
									</div>
								)}

								{/* Existing addresses */}
								{!addresses || addresses.length === 0 ? (
									!showAddAddress && (
										<p className="text-sm text-muted-foreground">No addresses on file.</p>
									)
								) : (
									addresses.map((address: Address) => (
										<AddressRow
											key={address.id}
											address={address}
											isEditing={editingAddressId === address.id}
											onEdit={() => {
												setShowAddAddress(false)
												setEditingAddressId(address.id)
											}}
											onEditDone={() => {
												setEditingAddressId(null)
												utils.address.getAllByFilter.invalidate()
											}}
											onDelete={() =>
												deleteAddress.mutate({ id: address.id, active: false })
											}
										/>
									))
								)}
							</CardContent>
						</Card>

						{/* Diagnosis — placeholder */}
						<PlaceholderCard title="Diagnosis" description="Diagnosis management coming soon." />

						{/* Referrals — placeholder */}
						<PlaceholderCard title="Referrals" description="Referral tracking coming soon." />

						{/* Primary Care Provider — placeholder */}
						<PlaceholderCard
							title="Primary Care Provider"
							description="Primary care provider details coming soon."
						/>
					</>
				)}
			</Container>
		</Page>
	)
}

/* ─── Address row ────────────────────────────────────────────────────────── */

function AddressRow({
	address,
	isEditing,
	onEdit,
	onEditDone,
	onDelete,
}: {
	address: Address
	isEditing: boolean
	onEdit: () => void
	onEditDone: () => void
	onDelete: () => void
}) {
	const label = ADDRESS_TYPE_LABELS[address.type] ?? address.type

	if (isEditing) {
		return (
			<div className="border rounded-lg p-4">
				<p className="text-sm font-medium mb-3">{label}</p>
				<AddressForm entity={address} onSuccess={onEditDone} />
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="mt-2"
					onClick={onEditDone}
				>
					Cancel
				</Button>
			</div>
		)
	}

	return (
		<div className="border rounded-lg p-4 flex items-start justify-between gap-4">
			<div className="flex flex-col gap-1 text-sm">
				<div className="flex items-center gap-2">
					<Badge variant="secondary" className="text-xs capitalize">
						{label}
					</Badge>
				</div>
				<p className="mt-1">{address.line1}</p>
				{address.line2 && <p>{address.line2}</p>}
				<p>
					{address.city}
					{address.state ? `, ${address.state}` : ""}{" "}
					{address.postalCode}
				</p>
				<p className="text-muted-foreground">{address.country}</p>
			</div>
			<div className="flex gap-2 shrink-0">
				<Button variant="outline" size="sm" onClick={onEdit}>
					Edit
				</Button>
				<Button
					variant="ghost"
					size="sm"
					className="text-destructive hover:text-destructive"
					onClick={onDelete}
				>
					Remove
				</Button>
			</div>
		</div>
	)
}

/* ─── Placeholder card ───────────────────────────────────────────────────── */

function PlaceholderCard({ title, description }: { title: string; description: string }) {
	return (
		<Card>
			<CardHeader>
				<CardTitle>{title}</CardTitle>
			</CardHeader>
			<CardContent>
				<p className="text-sm text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	)
}
